import React, { useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import '../../styles/Dashboard.css';
import CreateEmployee from '../Admin/CreateEmployee';
import Header from '../Common/Header';
import Sidebar from '../Common/Sidebar';
import AttendanceView from './AttendanceView';
import EmployeesView from './EmployeesView';
import MessagesView from './MessagesView';
import OverviewCard from './OverviewCards';
import PMTeamDashboard from './PMTeamDashboard';
import ProjectTeamAssignment from './ProjectTeamAssignment';
import ProjectsView from './ProjectsView';

const OwnerDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { currentUser } = useAuth();
  const { projects, users, tasks, attendance, exportProjects, exportTasks, exportAttendance, exportEmployees } = useData();
  const [showCreateEmployee, setShowCreateEmployee] = useState(false);

  const ownerMenuItems = [
    { name: 'Overview', path: '/dashboard', icon: 'home' },
    { name: 'Projects', path: '/dashboard/projects', icon: 'briefcase' },
    { name: 'Employees', path: '/dashboard/employees', icon: 'users' },
    { name: 'Attendance', path: '/dashboard/attendance', icon: 'clock' },
    { name: 'Messages', path: '/dashboard/messages', icon: 'message-square' },
    { name: 'Team Management', path: '/dashboard/team-management', icon: 'users-cog' },
    { name: 'Team Analytics', path: '/dashboard/team-analytics', icon: 'chart-line' }
  ];

  const stats = {
    totalProjects: projects.length,
    activeProjects: projects.filter(p => p.status === 'active').length,
    totalEmployees: users.filter(u => u.role !== 'owner').length,
    pendingTasks: tasks.filter(t => t.status !== 'completed').length,
    presentToday: (() => {
      const todayDate = new Date().toISOString().split('T')[0];
      const todayAttendance = attendance.filter(record => record.date === todayDate);
      return todayAttendance.filter(record => record.checkIn).length;
    })()
  };

  const DashboardHome = () => (
    <div className="dashboard-content">
      <div className="dashboard-headerr">
        <h2>Welcome back, {currentUser.name}!</h2>
        <p>Here's what's happening in your organization today.</p>
      </div>

      <OverviewCard stats={stats} />

      {/* Enhanced Action Cards */}
      <div className="enhanced-action-cards">
        <h3>Management Actions</h3>
        <div className="action-cards-grid">
          <div className="action-card" onClick={() => window.location.href = '/dashboard/team-management'}>
            <div className="action-icon">
              <i className="fas fa-users-cog"></i>
            </div>
            <div className="action-contentr">
              <h4 className="text-primaryy">Manage Project Teams</h4>
              <p className="text-secondary">Assign employees to projects and manage team compositions</p>
            </div>
            <div className="action-arrow">
              <i className="fas fa-arrow-right"></i>
            </div>
          </div>

          <div className="action-card" onClick={() => window.location.href = '/dashboard/team-analytics'}>
            <div className="action-icon">
              <i className="fas fa-chart-line"></i>
            </div>
            <div className="action-content">
              <h4 className="text-primary">Team Analytics</h4>
              <p className="text-secondary">View detailed analytics about team performance and attendance</p>
            </div>
            <div className="action-arrow">
              <i className="fas fa-arrow-right"></i>
            </div>
          </div>

          <div className="action-card" onClick={() => window.location.href = '/dashboard/projects'}>
            <div className="action-icon">
              <i className="fas fa-project-diagram"></i>
            </div>
            <div className="action-content">
              <h4 className="text-primary">View All Projects</h4>
              <p className="text-secondary">Manage and monitor all company projects</p>
            </div>
            <div className="action-arrow">
              <i className="fas fa-arrow-right"></i>
            </div>
          </div>

          <div className="action-card" onClick={() => window.location.href = '/dashboard/employees'}>
            <div className="action-icon">
              <i className="fas fa-users"></i>
            </div>
            <div className="action-content">
              <h4 className="text-primary">Manage Employees</h4>
              <p className="text-secondary">View and manage all company employees</p>
            </div>
            <div className="action-arrow">
              <i className="fas fa-arrow-right"></i>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-actions">
        <h3>Quick Actions</h3>
        <div className="action-buttons">
          <button 
            className="btn btn-primary"
            onClick={() => setShowCreateEmployee(true)}
          >
            <i className="fas fa-user-plus"></i>
            Create New Employee
          </button>
          <button className="btn btn-outline" onClick={() => exportProjects()}>
            <i className="fas fa-download"></i>
            Export Projects
          </button>
          <button className="btn btn-outline" onClick={() => exportTasks()}>
            <i className="fas fa-download"></i>
            Export Tasks
          </button>
          <button className="btn btn-outline" onClick={() => exportAttendance()}>
            <i className="fas fa-download"></i>
            Export Attendance
          </button>
          <button className="btn btn-outline" onClick={() => exportEmployees()}>
            <i className="fas fa-download"></i>
            Export Employees
          </button>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card">
          <h3>Recent Projects</h3>
          <div className="project-list">
            {projects.slice(0, 5).length > 0 ? (
              projects.slice(0, 5).map(project => (
                <div key={project.id} className="project-item">
                  <div className="project-info">
                    <h4 className="projectName">{project.name}</h4>
                    <span className={`status-badge status-${project.status}`}>
                      {project.status}
                    </span>
                    <p className="projectName">
                      Team: {project.teamMembers?.length || 0} members
                    </p>
                  </div>
                  <div className="project-progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${project.progress || 0}%` }}
                      ></div>
                    </div>
                    <span className="projectName">{project.progress || 0}%</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-data">
                <i className="fas fa-project-diagram"></i>
                <p className="projectName">No projects yet</p>
              </div>
            )}
          </div>
        </div>

        <div className="dashboard-card">
          <h3>Team Performance</h3>
          <div className="performance-list">
            {users.filter(u => u.role === 'employee').slice(0, 5).length > 0 ? (
              users.filter(u => u.role === 'employee').slice(0, 5).map(employee => {
                const employeeTasks = tasks.filter(t => t.assigneeId === employee.id);
                const completedTasks = employeeTasks.filter(t => t.status === 'completed');
                const completionRate = employeeTasks.length > 0 
                  ? Math.round((completedTasks.length / employeeTasks.length) * 100) 
                  : 0;

                // Check today's attendance
                const todayDate = new Date().toISOString().split('T')[0];
                const todayAttendance = attendance.find(
                  record => record.userId === employee.id && record.date === todayDate
                );

                return (
                  <div key={employee.id} className="performance-item">
                    <div className="employee-info">
                      <div className="employee-avatar">
                        {employee.name.charAt(0)}
                      </div>
                      <div>
                        <h4 className="projectName">{employee.name}</h4>
                        <span className="projectName">{employee.department}</span>
                        <div className="employee-status">
                          {todayAttendance?.checkIn ? (
                            <span className="status-present">
                              <i className="fas fa-check-circle"></i>
                              Present
                            </span>
                          ) : (
                            <span className="status-absent">
                              <i className="fas fa-times-circle"></i>
                              Absent
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="completion-rate">
                      <span className="projectName">{completionRate}%</span>
                      <small className="text-muted">completion</small>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-data">
                <i className="fas fa-users"></i>
                <p className="text-secondary">No employees yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Today's Stats */}
      <div className="dashboard-card">
        <h3>Today's Overview</h3>
        <div className="today-stats">
          <div className="today-stat">
            <i className="fas fa-user-check"></i>
            <div>
              <span className="stat-number text-primary">{stats.presentToday}</span>
              <span className="stat-label text-secondary">Present Today</span>
            </div>
          </div>
          <div className="today-stat">
            <i className="fas fa-tasks"></i>
            <div>
              <span className="stat-number text-primary">{tasks.filter(t => t.status === 'ongoing').length}</span>
              <span className="stat-label text-secondary">Active Tasks</span>
            </div>
          </div>
          <div className="today-stat">
            <i className="fas fa-project-diagram"></i>
            <div>
              <span className="stat-number text-primary">{stats.activeProjects}</span>
              <span className="stat-label text-secondary">Active Projects</span>
            </div>
          </div>
          <div className="today-stat">
            <i className="fas fa-envelope"></i>
            <div>
              <span className="stat-number text-primary">{stats.unreadMessages || 0}</span>
              <span className="stat-label text-secondary">Unread Messages</span>
            </div>
          </div>
        </div>
      </div>

      {/* Create Employee Modal */}
      {showCreateEmployee && (
        <CreateEmployee 
          onClose={() => setShowCreateEmployee(false)} 
        />
      )}
    </div>
  );

  return (
    <div className="dashboard-layout">
      <Sidebar 
        menuItems={ownerMenuItems}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="dashboard-main">
        <Header 
          title="Owner Dashboard"
          onMenuClick={() => setSidebarOpen(true)}
        />

        <main className="dashboard-body">
          <Routes>
            <Route path="/" element={<DashboardHome />} />
            <Route path="/projects" element={<ProjectsView />} />
            <Route path="/employees" element={<EmployeesView />} />
            <Route path="/attendance" element={<AttendanceView />} />
            <Route path="/messages" element={<MessagesView />} />
            <Route path="/team-management" element={<ProjectTeamAssignment />} />
            <Route path="/team-analytics" element={<PMTeamDashboard />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default OwnerDashboard;
